import { Button as MantineButton, MantineProvider } from '@mantine/core';
import { useFocusWithin } from '@mantine/hooks';
import cx from 'clsx';
import { FC, memo, PropsWithChildren } from 'react';

import styles from './styles.module.css';
import { theme } from './theme';

type Props = {
  variant?: 'filled' | 'outline' | 'transparent' | 'danger';
  disabled?: boolean;
  loading?: boolean;
  round?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  type?: 'button' | 'submit';
  icon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  onBlur?: () => void;
  onClick?: () => void;
  onFocus?: () => void;
};

export const Button: FC<PropsWithChildren<Props>> = memo(
  ({
    children,
    disabled = false,
    icon,
    loading = false,
    round = false,
    type = 'submit',
    size = 'md',
    variant = 'filled',
    fullWidth = false,
    onBlur,
    onClick,
    onFocus,
    className,
  }) => {
    const { ref, focused } = useFocusWithin();

    return (
      <MantineProvider theme={theme}>
        <MantineButton
          type={type}
          classNames={{
            loader: cx({
              [styles.loader]: true,
              [styles.loaderFilled]: variant === 'filled',
              [styles.loaderOutline]: variant === 'outline',
              [styles.loaderTransparent]: variant === 'transparent',
              [styles.loaderDanger]: variant === 'danger',
            }),
            root: cx({
              [styles.button]: true,
              [styles.buttonFilled]: variant === 'filled',
              [styles.buttonOutline]: variant === 'outline',
              [styles.buttonTransparent]: variant === 'transparent',
              [styles.buttonDanger]: variant === 'danger',
              [styles.buttonFullWidth]: fullWidth,

              [styles.buttonFilledFocused]: variant === 'filled' && focused,
              [styles.buttonOutlineFocused]: variant === 'outline' && focused,
              [styles.buttonTransparentFocused]:
                variant === 'transparent' && focused,
              [styles.buttonDangerFocused]: variant === 'danger' && focused,

              [styles.buttonFilledDisabled]: variant === 'filled' && disabled,
              [styles.buttonOutlineDisabled]: variant === 'outline' && disabled,
              [styles.buttonTransparentDisabled]:
                variant === 'transparent' && disabled,
              [styles.buttonDangerDisabled]: variant === 'danger' && disabled,
              [className || '']: true,
            }),
          }}
          disabled={disabled || loading}
          loading={loading}
          radius={round ? '50px' : '5px'}
          ref={ref}
          rightSection={icon ?? undefined}
          size={size}
          variant={variant === 'danger' ? 'filled' : variant}
          onBlur={onBlur}
          onClick={onClick}
          onFocus={onFocus}
        >
          {children}
        </MantineButton>
      </MantineProvider>
    );
  }
);
